const TRACKING_PARAMS_SESSION_KEY = 'quiz_utm_params';

type RedirectParamOptions = {
  /** Always set these params (overwrite any existing value). */
  alwaysParams?: Record<string, string | undefined>;
  /** Only set these params when the INCOMING (preserved) params do NOT contain the key. */
  missingParams?: Record<string, string | undefined>;
};

/**
 * Collect the original landing-page params that were stored at quiz start,
 * merged with whatever is still on the current URL.
 */
function getIncomingParams(): URLSearchParams {
  const incoming = new URLSearchParams();

  // 1. Start with what was stored at quiz-start
  try {
    const stored = sessionStorage.getItem(TRACKING_PARAMS_SESSION_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Record<string, string>;
      for (const [key, value] of Object.entries(parsed)) {
        if (value) incoming.set(key, value);
      }
    }
  } catch {
    // ignore
  }

  // 2. Layer current URL params on top (they win if both exist)
  const current = new URLSearchParams(window.location.search);
  current.forEach((value, key) => {
    incoming.set(key, value);
  });

  return incoming;
}

/**
 * Build a redirect URL that:
 * 1. Starts with the destination URL
 * 2. Copies ALL incoming (preserved) tracking params onto it
 * 3. Applies `missingParams` — only if the key is NOT already present from incoming
 * 4. Applies `alwaysParams` — unconditionally (always wins)
 */
export function redirectWithParams(
  destinationUrl: string,
  options: RedirectParamOptions = {},
): string {
  console.log('[redirectWithParams] v3 loaded');
  if (typeof window === 'undefined') return destinationUrl;

  const url = new URL(destinationUrl, window.location.origin);
  const incoming = getIncomingParams();

  // Step 1 — copy all incoming params onto the destination
  incoming.forEach((value, key) => {
    url.searchParams.set(key, value);
  });

  // Step 2 — set fallback params ONLY when the incoming set did NOT contain the key
  for (const [key, value] of Object.entries(options.missingParams ?? {})) {
    if (value && !incoming.has(key)) {
      url.searchParams.set(key, value);
    }
  }

  // Step 3 — force-set always-params (these always win, no matter what)
  for (const [key, value] of Object.entries(options.alwaysParams ?? {})) {
    if (value) {
      url.searchParams.set(key, value);
    }
  }

  console.debug('[redirectWithParams] final URL:', url.toString());
  return url.toString();
}
